import { SignedProof } from '@civic/civic-sign';
import { CustomError } from '../../common/errors';
import Result from '../../common/result';
import UserTokenSettable from '../common/userTokenSettable';
import { ValidationStep } from '../validation';

export enum ExchangeTokenStatus {
  pending = 'pending',
  activated = 'activated',
}

export type Authentication = {
  token: string;
};

export type AuthenticateRequest = {
  did: string;
  address: string;
  proof: SignedProof;
  chain: 'solana' | 'ethereum' | 'casper' | 'icp';
  nonceTimestamp: number;
  network?: string;
};

export type UserFeedbackOptions = 'positive' | 'negative';

export type UserFeedbackRequest = {
  feedback: UserFeedbackOptions;
  networkAddress: string;
  did?: string;
  validationId?: string;
  flowId?: string;
  wallet?: string;
  domain?: string;
};

export type ExchangeTokenData = {
  did: string;
  address: string;
  networkAddress: string;
  validationId: string;
  steps: ValidationStep[];
  sourceAction: string;
  reuseCredentials?: boolean;
  scopeRequestId?: string;
};

export type ExchangeToken = {
  exchangeToken: string;
  token: string;
  status: ExchangeTokenStatus;
  context: ExchangeTokenData;
};

export type Nonce = {
  nonce: string;
  timestamp: number;
};

export type ExchangeTokenRequest = {
  exchangeToken: string;
};

export type CreateExchangeTokenRequest = {
  networkAddress: string;
  validationId: string;
  steps: ValidationStep[];
  sourceAction: string;
  reuseCredentials?: boolean;
  scopeRequestId?: string;
};

export type AuthenticationResult = Result<CustomError, Authentication>;
export type ExchangeTokenResult = Result<CustomError, ExchangeToken>;
export type NonceResult = Result<CustomError, Nonce>;
export type AuthenticateExchangeTokenResult = Result<CustomError, ExchangeToken>;
export type UserFeedbackResult = Result<CustomError>;

export interface AuthenticationService extends UserTokenSettable {
  authenticate(request: AuthenticateRequest): Promise<AuthenticationResult>;
  getNonce(): Promise<NonceResult>;
  authenticateExchangeToken(request: ExchangeTokenRequest): Promise<AuthenticateExchangeTokenResult>;
  requestExchangeToken(request: CreateExchangeTokenRequest): Promise<ExchangeTokenResult>;
  getExchangeToken(request: ExchangeTokenRequest): Promise<ExchangeTokenResult>;
  submitUserFeedback(request: UserFeedbackRequest): Promise<UserFeedbackResult>;
}
